import { useCallback, useMemo } from 'react';
import { ConversionFactorUnit, Results } from '@shared/models/unit.model';
import { useResolvedVersion } from '@client/hooks/use-resolved-version.hooks';
import { useSelectedProposalId } from '@client/hooks/use-selected-proposal-id.hooks';
import { useSelectedElementId } from '@client/store/ui/ui.hook';
import { getMaxValuesInArray } from '@shared/helpers/math.helpers';
import { useGroupByCategory } from '@features/products/group-by-category.store';
import { useFilterResultsBySelectedLifecycles } from '@features/results/use-results.hooks';
import { createLocalStorageStore } from '@client/helpers/local-storage.helpers';
import { useProjectBuildingGFA } from '@client/store/project';
import { getResultsByFactor } from '@shared/helpers/results.helpers';
import { useSharedMemo } from '@client/hooks/use-utils.hooks';
import {
  createProductListCategoryGroups,
  createProductListGroups,
} from './product-list-build.helpers';
import { useProposalResultsRecord } from '@features/proposals/use-proposals.hooks';
import { useEffectiveSbefGroups } from '@features/sbef/use-effective-sbef-groups.hook';

export {
  createProductListCategoryGroups,
  createProductListGroups,
} from './product-list-build.helpers';

export const {
  useStore: useProductListEmissionsPerMass,
  set: setProductListEmissionsPerMass,
} = createLocalStorageStore('product_list_emissions_per_mass', false);

export const useProductListCategoryGroups = () => {
  const version = useResolvedVersion();
  const selectedProposalId = useSelectedProposalId();
  const resultsRecord = useProposalResultsRecord(undefined, {
    applySbefFilter: false,
  });
  const effectiveSbefGroups = useEffectiveSbefGroups();

  return useSharedMemo(
    () =>
      version
        ? createProductListCategoryGroups(
            version,
            resultsRecord,
            effectiveSbefGroups,
          )
        : [],
    `useProductListCategoryGroups:${version?.id ?? 'none'}:${selectedProposalId ?? 'baseline'}:${effectiveSbefGroups.join(',')}`,
    [version, resultsRecord, selectedProposalId, effectiveSbefGroups],
  );
};

export const useProductListGroups = () => {
  const version = useResolvedVersion();
  const selectedProposalId = useSelectedProposalId();
  const resultsRecord = useProposalResultsRecord(undefined, {
    applySbefFilter: false,
  });
  const effectiveSbefGroups = useEffectiveSbefGroups();

  return useSharedMemo(
    () =>
      version
        ? createProductListGroups(
            version,
            [],
            {},
            resultsRecord,
            effectiveSbefGroups,
          )
        : [],
    `useProductListGroups:${version?.id ?? 'none'}:${selectedProposalId ?? 'baseline'}:${effectiveSbefGroups.join(',')}`,
    [version, resultsRecord, selectedProposalId, effectiveSbefGroups],
  );
};

export const useSelectedProductListElement = () => {
  const selectedElementId = useSelectedElementId();

  const productListCategoryGroups = useProductListCategoryGroups();
  const productListGroups = useProductListGroups();
  const groupByCategory = useGroupByCategory();

  const groups = useMemo(
    () =>
      groupByCategory
        ? productListCategoryGroups.flatMap((g) => g.elements)
        : productListGroups,
    [groupByCategory, productListCategoryGroups, productListGroups],
  );

  const productItems = useMemo(
    () => groups.flatMap((g) => g.elements),
    [groups],
  );

  const selectableElements = useMemo(
    () =>
      groupByCategory
        ? [...productListCategoryGroups, ...groups, ...productItems]
        : [...groups, ...productItems],
    [groupByCategory, groups, productItems, productListCategoryGroups],
  );

  return useMemo(
    () => selectableElements.find((g) => g.id === selectedElementId),
    [selectableElements, selectedElementId],
  );
};

export const useProductListMaxResults = (factorized = false): Results => {
  const lifecycleFilter = useFilterResultsBySelectedLifecycles();
  const getResultsDivisor = useProductListItemResultsDivisor();

  const groupByCategory = useGroupByCategory();
  const categoryGroups = useProductListCategoryGroups();
  const groups = useProductListGroups();

  const results = useMemo(
    () =>
      (groupByCategory ? categoryGroups : groups).map(({ results, unit }) => {
        const divisor = factorized ? getResultsDivisor(unit, results) : 1;
        return getResultsByFactor(results, divisor);
      }),
    [groupByCategory, categoryGroups, groups, factorized, getResultsDivisor],
  );

  return useMemo(
    () => lifecycleFilter(getMaxValuesInArray(results)),
    [lifecycleFilter, results],
  );
};

export const useProductListItemResultsDivisor = () => {
  const isPerUnit = useProductListEmissionsPerMass();
  const gfa = useProjectBuildingGFA();

  return useCallback(
    (unit: ConversionFactorUnit, results: Results | undefined) => {
      if (!results) {
        return 1;
      }
      const unitDivisor = results[unit] ?? 1;
      return isPerUnit ? unitDivisor : gfa;
    },
    [isPerUnit, gfa],
  );
};
